import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
} from '@mui/material'
import {
  ArrowBack,
  HealthAndSafety,
  Medication,
  Assignment,
  Science,
  LocalHospital,
  Info,
  CheckCircle,
  CalendarToday,
  Edit,
  TrendingUp,
} from '@mui/icons-material'

const HealthProfile: React.FC = () => {
  const navigate = useNavigate()

  const healthCategories = [
    {
      icon: <HealthAndSafety fontSize="large" />,
      title: 'Diseases & Conditions',
      color: '#e74c3c',
      features: [
        'Track all diagnoses and conditions',
        'Record diagnosis dates',
        'Mark primary diagnosis',
        'Track ongoing vs. resolved conditions',
        'Add detailed notes and history',
      ]
    },
    {
      icon: <Medication fontSize="large" />,
      title: 'Medications',
      color: '#3498db',
      features: [
        'List all current and past medications',
        'Track dosage and frequency',
        'Record start and end dates',
        'Note side effects and effectiveness',
        'Identify medications for primary condition',
      ]
    },
    {
      icon: <Assignment fontSize="large" />,
      title: 'Symptoms',
      color: '#f39c12',
      features: [
        'Document all symptoms',
        'Track symptom timeline',
        'Rate severity and frequency',
        'Link symptoms to conditions',
        'Monitor changes over time',
      ]
    },
    {
      icon: <Science fontSize="large" />,
      title: 'Test Results & Labs',
      color: '#9b59b6',
      features: [
        'Store lab results and values',
        'Upload test reports',
        'Track abnormal results',
        'Monitor trends over time',
        'Link results to conditions',
      ]
    },
    {
      icon: <LocalHospital fontSize="large" />,
      title: 'Procedures & Surgeries',
      color: '#1abc9c',
      features: [
        'Record all procedures',
        'Track surgery dates',
        'Note outcomes and complications',
        'Document recovery timeline',
        'Link to primary diagnosis',
      ]
    },
    {
      icon: <Info fontSize="large" />,
      title: 'Other Health Data',
      color: '#95a5a6',
      features: [
        'BMI calculator with unit conversion',
        'Vital signs tracking',
        'Family medical history',
        'Allergies and reactions',
        'Lifestyle factors',
      ]
    },
  ]

  const benefits = [
    'Complete medical history in one place',
    'Easy to update and maintain',
    'Share with healthcare providers',
    'Export data when needed',
    'Track health trends over time',
    'Secure and private',
  ]

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        <Button 
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Home
        </Button>

        <Paper sx={{ p: 4 }}>
          <Box textAlign="center" mb={6}>
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              Comprehensive Health Profile
            </Typography>
            <Typography variant="h5" color="text.secondary">
              Organize and track all your health information in one secure place
            </Typography>
          </Box>

          {/* Hero Section */}
          <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 4, borderRadius: 3, mb: 6 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Your Complete Health Story
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Build a comprehensive digital health profile that captures every aspect of your medical 
                  journey. From diagnoses to medications, symptoms to test results - everything organized 
                  and easily accessible.
                </Typography>
                <Button 
                  variant="contained" 
                  size="large"
                  sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                  onClick={() => navigate('/register')}
                >
                  Start Building Your Profile
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  {benefits.map((benefit, index) => (
                    <Box key={index} display="flex" alignItems="center" gap={1}>
                      <CheckCircle />
                      <Typography variant="body1">{benefit}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Box>

          {/* Health Categories */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6, mb: 3 }}>
            Six Comprehensive Categories
          </Typography>
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {healthCategories.map((category, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ 
                  height: '100%', 
                  borderLeft: 4, 
                  borderColor: category.color,
                  '&:hover': { boxShadow: 6 }
                }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Box sx={{ color: category.color }}>
                        {category.icon}
                      </Box>
                      <Typography variant="h5" fontWeight="bold">
                        {category.title}
                      </Typography>
                    </Box>
                    <List dense>
                      {category.features.map((feature, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircle color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Features */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6, mb: 3 }}>
            Powerful Management Features
          </Typography>
          <Grid container spacing={3} sx={{ mb: 6 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
                <Edit sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Full CRUD Operations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create, read, update, and delete any health record. Complete control over your data.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
                <CalendarToday sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Timeline Tracking
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track start and end dates for all health events. See your health journey over time.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
                <TrendingUp sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Trend Analysis
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Monitor changes and patterns in your health data over time.
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* How to Use */}
          <Box sx={{ bgcolor: 'grey.50', p: 4, borderRadius: 3, mb: 6 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom textAlign="center">
              Easy to Use
            </Typography>
            <Grid container spacing={4} sx={{ mt: 2 }}>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="primary" fontWeight="bold">1</Typography>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Choose Category
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select from six organized health categories
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="primary" fontWeight="bold">2</Typography>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Add Records
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enter health data using AI or manual entry
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="primary" fontWeight="bold">3</Typography>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Organize & Edit
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Review, edit, and organize your information
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="primary" fontWeight="bold">4</Typography>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Match Trials
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use your profile to find relevant clinical trials
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Example Data */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6, mb: 3 }}>
            What You Can Track
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  For Each Condition
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label="Diagnosis Date" size="small" />
                  <Chip label="Current Status" size="small" />
                  <Chip label="Primary Diagnosis" size="small" />
                  <Chip label="Treatment Status" size="small" />
                  <Chip label="Notes & History" size="small" />
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  For Each Medication
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label="Dosage & Unit" size="small" />
                  <Chip label="Frequency" size="small" />
                  <Chip label="Start/End Dates" size="small" />
                  <Chip label="Side Effects" size="small" />
                  <Chip label="Purpose" size="small" />
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* CTA */}
          <Box textAlign="center" sx={{ mt: 6 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Ready to Build Your Health Profile?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Start organizing your health information today
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/register')}
              sx={{ px: 6, py: 2 }}
            >
              Get Started Free
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}

export default HealthProfile

